import { createContext, type ReactNode } from 'react';
import { type FirebaseApp, getApps, initializeApp } from 'firebase/app';
import { initializeAppCheck, ReCaptchaV3Provider } from 'firebase/app-check';
import { connectStorageEmulator, getStorage } from 'firebase/storage';

// Publishable client config for Firebase Web apps - not a secret, safe to check in.
// See https://firebase.google.com/docs/projects/api-keys
const firebaseConfig = {
  apiKey: 'AIzaSyBBH4ZkCAYouTA_Tsb37CbuqNxMki49M5Q',
  authDomain: 'auto-market-info.firebaseapp.com',
  databaseURL: 'https://auto-market-info-default-rtdb.europe-west1.firebasedatabase.app',
  projectId: 'auto-market-info',
  storageBucket: 'auto-market-info.appspot.com',
  messagingSenderId: '95206966334',
  appId: '1:95206966334:web:ee6b83d19e6991562d3d6f',
  measurementId: 'G-BMW366CC3L',
};

const APP_CHECK_SITE_KEY = '6LfFWR0pAAAAAPEhEnGsGMVUT8V4mMcEeb1M_5RO';

// Fixed (not auto-generated) App Check debug token for local dev - reCAPTCHA v3 can't attest
// localhost, so App Check would reject every Storage request without one. A hard-coded token
// (vs. `true`, which mints a new random one per browser profile) means every local profile -
// your everyday browser, VS Code's "Launch Chrome" debug profile, a CI/headless browser - shares
// one token, so it only has to be registered once instead of per-profile. Register it in
// Firebase console > App Check > auto-market-info (web) > Manage debug tokens.
const APP_CHECK_DEBUG_TOKEN = 'e3a635cf-0a2b-44c5-9306-c7072c646ca6';

export const FirebaseContext = createContext<FirebaseApp | null>(null);

function getOrInitializeApp(): FirebaseApp {
  const [existingApp] = getApps();
  if (existingApp) {
    return existingApp;
  }

  if (process.env.NODE_ENV === 'development') {
    (window as unknown as { FIREBASE_APPCHECK_DEBUG_TOKEN?: string }).FIREBASE_APPCHECK_DEBUG_TOKEN =
      APP_CHECK_DEBUG_TOKEN;
  }

  const app = initializeApp(firebaseConfig);

  // Opt-in escape hatch for developing against the local Storage emulator (see
  // mock-data/seed-emulator.mjs) instead of the live project - e.g. while it's suspended.
  // App Check is skipped entirely in this mode: the emulator doesn't enforce it, and
  // initializing it would just add a reCAPTCHA round-trip for no benefit.
  if (process.env.REACT_APP_USE_FIREBASE_EMULATOR === 'true') {
    connectStorageEmulator(getStorage(app), '127.0.0.1', 9199);
    return app;
  }

  initializeAppCheck(app, {
    provider: new ReCaptchaV3Provider(APP_CHECK_SITE_KEY),
    isTokenAutoRefreshEnabled: true,
  });
  return app;
}

export function FirebaseProvider({ children }: { children: ReactNode }) {
  const app = getOrInitializeApp();
  return <FirebaseContext.Provider value={app}>{children}</FirebaseContext.Provider>;
}
